import classNames from 'classnames';
import { useState, useRef, MouseEvent, TouchEvent, useEffect } from 'react';

import { ReactComponent as IconDot } from '../../assets/icons/svg/switch-dot.svg';

type SwitchFieldProps<T> = {
    onChange: (value: T | undefined) => void;
    value: T | undefined;
    options: { value: T; label: string }[];
    label: string;
    withManualInput?: boolean;
    minValue?: number;
    large?: boolean;
};

export const SwitchField = ({
    onChange,
    value,
    label,
    options,
    withManualInput,
    minValue = 1,
    large,
}: SwitchFieldProps<number>) => {
    const [isDragging, setIsDragging] = useState(false);
    const trackRef = useRef<HTMLDivElement>(null);

    const getValueFromPosition = (clientX: number) => {
        if (!trackRef.current || options.length === 0) return value;

        const trackRect = trackRef.current.getBoundingClientRect();
        const position = clientX - trackRect.left;
        const trackWidth = trackRect.width;
        
        if (trackWidth <= 0) return value;

        // Make the first option more accessible by giving it a bit more space
        if (position <= trackWidth / (options.length * 2)) {
            return options[0]?.value ?? value;
        }

        const percentage = Math.max(0, Math.min(1, position / trackWidth));
        let calculatedIndex = Math.floor(percentage * options.length);
        calculatedIndex = Math.max(0, Math.min(options.length - 1, calculatedIndex));

        let newValue = options[calculatedIndex]?.value;

        if (newValue !== undefined && newValue < minValue) {
            const firstValidOption = options.find(o => o.value >= minValue);
            newValue = firstValidOption?.value ?? newValue;
        }

        return newValue ?? value;
    };

    const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
        setIsDragging(true);
        const newValue = getValueFromPosition(e.clientX);
        if (newValue !== undefined && newValue !== value) {
            onChange(newValue);
        }
        e.preventDefault();
    };

    const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
        setIsDragging(true);
        const touch = e.touches[0];
        const newValue = getValueFromPosition(touch.clientX);
        if (newValue !== undefined && newValue !== value) {
            onChange(newValue);
        }
        e.preventDefault(); // Prevent scrolling while dragging
    };

    const handleMouseUpOrLeave = () => {
        if (isDragging) {
            setIsDragging(false);
        }
    };

    useEffect(() => {
        const handleGlobalMouseMove = (e: globalThis.MouseEvent) => {
            if (!isDragging) return;

            const newValue = getValueFromPosition(e.clientX);
            if (newValue !== undefined && newValue !== value) {
                onChange(newValue);
            }
        };

        const handleGlobalTouchMove = (e: globalThis.TouchEvent) => {
            if (!isDragging) return;

            const touch = e.touches[0];
            const newValue = getValueFromPosition(touch.clientX);
            if (newValue !== undefined && newValue !== value) {
                onChange(newValue);
            }
            e.preventDefault(); // Prevent scrolling while dragging
        };

        const handleGlobalMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
            }
        };

        const handleGlobalTouchEnd = () => {
            if (isDragging) {
                setIsDragging(false);
            }
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleGlobalMouseMove);
            window.addEventListener('mouseup', handleGlobalMouseUp);
            window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
            window.addEventListener('touchend', handleGlobalTouchEnd);
            window.addEventListener('touchcancel', handleGlobalTouchEnd);
        }

        return () => {
            window.removeEventListener('mousemove', handleGlobalMouseMove);
            window.removeEventListener('mouseup', handleGlobalMouseUp);
            window.removeEventListener('touchmove', handleGlobalTouchMove);
            window.removeEventListener('touchend', handleGlobalTouchEnd);
            window.removeEventListener('touchcancel', handleGlobalTouchEnd);
        };
    }, [isDragging, onChange, value, options.length]);

    return (
        <div className="overflow-hidden -mx-2">
            <div
                className={classNames('text-gray-150 pb-2 mb-0.5 pl-2', [
                    large ? 'text-xs' : 'text-xxs',
                ])}
            >
                {label}
            </div>

            {/*<div className="h-0.5 bg-gray-650 w-full" />*/}
            <div className="flex justify-between relative pl-2">
                <div
                    ref={trackRef}
                    className="flex justify-between relative flex-1 select-none cursor-pointer touch-none"
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUpOrLeave}
                    onTouchStart={handleTouchStart}
                >
                    {options.map((o, i) => (
                        <div
                            key={o.value}
                            onClick={() => {
                                if (!isDragging) {
                                    onChange(o.value);
                                }
                            }}
                            className={classNames(
                                'relative flex flex-1 flex-col items-center switch-item-line pointer-events-none',
                                {
                                    fill: value !== undefined && o.value < value,
                                    active: o.value === value,
                                },
                                [large ? 'text-xs' : 'text-xxs']
                            )}
                        >
                            {/*<div className='absolute -top-1 w-full h-0.5 bg-white' />*/}
                            {o.value === value && (
                                <div className="switch-item-line_dot">
                                    <IconDot className="switch-item-line_dot_icon  -ml-0.5" />
                                </div>
                            )}
                            <div
                                className="switch-item-line-label -ml-1"
                                style={{ letterSpacing: '-0.1em', padding: '6px 0 0' }}
                            >
                                {o.label}
                            </div>
                        </div>
                    ))}
                </div>

                {withManualInput && (
                    <div className="w-10 ml-1 mr-3 -mt-1 relative -top-0.5">
                        <input
                            type="number"
                            className={classNames('text-black input-text text-center', [
                                large ? 'text-xs' : 'text-xxs',
                            ])}
                            style={{ padding: '4px 4px', letterSpacing: '-0.1em' }}
                            onChange={e => {
                                if (e.target.value) {
                                    onChange(+e.target.value);
                                } else {
                                    onChange(undefined);
                                }
                            }}
                            value={value ?? ''}
                            onBlur={() => {
                                if (value === undefined || value < minValue) {
                                    onChange(minValue);
                                }
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
